import React from 'react';
import { Checkbox, Typography } from 'antd';
import { defineMessages, FormattedMessage } from 'react-intl';
import { getLocalStorage } from 'shared/utils/localStorage';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import globalMessages from 'translations/messages';

const messages = defineMessages({
  title: 'Send data to an external AI service?',
  disclaimer: 'The data will be sent to an external AI provider. Automatic masking may miss personal or sensitive information. You are responsible for reviewing the data and ensuring that it may be shared. Do not continue if it contains information that must not be sent. For ongoing AI tasks, this also applies to future data sent by the task.',
  dontShowAgain: "Don't show again on this browser",
  confirm: 'Continue and send data',
});

const useExternalAIConfirmation = () => {
  const { modal } = useGlobalContext();
  const { user } = useUser();

  return async () => {
    const preferenceKey = user?.id && `external-ai-confirmation:v1:${user.id}`;
    try {
      if (preferenceKey && getLocalStorage().getItem(preferenceKey) === 'confirmed') return true;
    } catch (_) { /* Storage can be unavailable in private browsers. */ }

    let dontShowAgain = false;
    return new Promise(resolve => {
      modal.confirm({
        title: <FormattedMessage {...messages.title} />,
        content: (
          <>
            <Typography.Paragraph><FormattedMessage {...messages.disclaimer} /></Typography.Paragraph>
            <Checkbox onChange={event => { dontShowAgain = event.target.checked; }}>
              <FormattedMessage {...messages.dontShowAgain} />
            </Checkbox>
          </>
        ),
        okText: <FormattedMessage {...messages.confirm} />,
        cancelText: <FormattedMessage {...globalMessages.cancel} />,
        onOk: () => {
          if (dontShowAgain && preferenceKey) {
            try { getLocalStorage().setItem(preferenceKey, 'confirmed'); } catch (_) { /* Confirm this action even if the preference cannot be saved. */ }
          }
          resolve(true);
        },
        onCancel: () => resolve(false),
        afterClose: () => resolve(false),
      });
    });
  };
};

export default useExternalAIConfirmation;
